import { useMemo } from 'react';
import { filterImagesBySelections, filterImagesByGalleries } from 'helpers/filterImages';
import { getTitleImage } from 'helpers/getTitleImage';
import { moveArrayItem } from 'helpers/moveArrayItem';
import { IGallery, IndexedImage } from 'old-store/types/gallery';
import { useAppSelector } from 'hooks/redux';
import { selectSortedGalleries } from 'store/slices/collection/selectors';
import { selectActiveSelections } from 'store/selectors/activeSelection';
import { filterDigitalGifs } from 'modules/ScrShopNew/helpers/filtering';
import { useParams, useLocation } from 'react-router-dom';

interface IImageMap {
  [key: string]: IndexedImage[];
}

export interface IIndexedImages {
  filteredImagesArr: IndexedImage[];
  filteredImagesMap: IImageMap;
  selectionImagesArr?: IndexedImage[];
  selectionImagesMap?: IImageMap;
  galleryImagesArr?: IndexedImage[];
  galleryImagesMap?: IImageMap;
}

interface IIndexedProps {
  galleriesItems?: IGallery[];
}

interface ISelectionImagesData {
  filteredImagesArr: IndexedImage[];
  filteredImagesMap: IImageMap;
}

interface IGalleryImagesData {
  filteredImagesArr: IndexedImage[];
  filteredImagesMap: IImageMap;
}

interface ISelectPhotoImagesData extends IIndexedImages {}

interface IFallbackImages {
  filteredImagesArr: IndexedImage[];
  filteredImagesMap: IImageMap;
}

const processImagesWithTitle = (
  images: IndexedImage[],
  galleryId: string,
  collectionType: string
): IndexedImage[] => {
  const { titleImage } = getTitleImage(images, collectionType);
  const updatedImages = [...images];

  if (titleImage) {
    const titleImageIdx = updatedImages.findIndex((img) => img._id === titleImage._id);
    moveArrayItem(updatedImages, titleImageIdx, 0);
  }

  return updatedImages.map((img, idx) => ({
    ...img,
    index: idx,
    imageId: `${galleryId}_${idx}`
  }));
};

const indexImages = (imageGroups: IImageMap): { arr: IndexedImage[]; map: IImageMap } => {
  const indexedMap: IImageMap = {};
  const indexedArr: IndexedImage[] = [];

  Object.entries(imageGroups).forEach(([galleryId, images]) => {
    const indexedGalleryImages = images.map((img, index) => ({
      ...img,
      index,
      imageId: `${galleryId}_${index}`
    }));
    indexedMap[galleryId] = indexedGalleryImages;
    indexedArr.push(...indexedGalleryImages);
  });

  return { arr: indexedArr, map: indexedMap };
};

export const useIndexedImages = ({ galleriesItems }: IIndexedProps): IIndexedImages => {
  const { groupSlug = '' } = useParams<{ groupSlug: string }>();
  const { collection, images } = useAppSelector((state) => ({
    collection: state.collection,
    images: state.images.images
  }));
  const selection = useAppSelector(selectActiveSelections);
  const galleries = useAppSelector(selectSortedGalleries);
  const currentGalleries = galleriesItems || galleries;
  const { pathname } = useLocation();

  const isSelectPhotoPage = pathname.includes('/select-photo');
  const isSelectionPage = pathname.includes('/selections');

  const filteredImagesByGallery = useMemo(
    () => filterImagesByGalleries(currentGalleries, images),
    [currentGalleries, images]
  );

  const selectionImagesData = useMemo<ISelectionImagesData | null>(() => {
    if (!isSelectionPage) return null;
    const allFilteredImages = Object.values(filteredImagesByGallery).flat();
    const selectedImagesMap = filterImagesBySelections(allFilteredImages, selection);
    const selectedImagesArr = Object.values(selectedImagesMap).flat() as IndexedImage[];

    return {
      filteredImagesArr: selectedImagesArr,
      filteredImagesMap: selectedImagesMap
    };
  }, [isSelectionPage, filteredImagesByGallery, selection]);

  const galleryImagesData = useMemo<IGalleryImagesData | null>(() => {
    if (isSelectionPage) return null;
    const updatedFilteredImagesMap = Object.entries(filteredImagesByGallery).reduce<IImageMap>(
      (acc, [galleryId, images]) => ({
        ...acc,
        [galleryId]: processImagesWithTitle(images, galleryId, collection.type)
      }),
      {}
    );
    const updatedImagesArr = Object.values(updatedFilteredImagesMap).flat() as IndexedImage[];

    return {
      filteredImagesArr: updatedImagesArr,
      filteredImagesMap: updatedFilteredImagesMap
    };
  }, [isSelectionPage, filteredImagesByGallery, collection.type]);

  const selectPhotoImagesData = useMemo<ISelectPhotoImagesData | null>(() => {
    if (!isSelectPhotoPage) return null;

    const filteredForGroup = Object.entries(filteredImagesByGallery).reduce<IImageMap>(
      (acc, [galleryId, images]) => ({
        ...acc,
        [galleryId]: images.filter((img: any) => filterDigitalGifs(img, groupSlug))
      }),
      {}
    );

    const { arr: allFilteredArr } = indexImages(filteredForGroup);
    const selectedImagesMap = filterImagesBySelections(allFilteredArr, selection);
    const filteredSelectionImagesArr = Object.values(selectedImagesMap).flat() as IndexedImage[];
    const filteredSelectionImagesMap = Object.entries(selectedImagesMap).reduce<IImageMap>(
      (acc, [key, images]) => ({
        ...acc,
        [key]: images.filter((img: any) => filterDigitalGifs(img, groupSlug))
      }),
      {}
    );

    const filteredGalleryImagesMapWithTitle = Object.entries(filteredForGroup).reduce<IImageMap>(
      (acc, [galleryId, images]) => ({
        ...acc,
        [galleryId]: processImagesWithTitle(images, galleryId, collection.type)
      }),
      {}
    );
    const filteredGalleryImagesArr = Object.values(
      filteredGalleryImagesMapWithTitle
    ).flat() as IndexedImage[];

    const combinedImagesArr = [...filteredSelectionImagesArr, ...filteredGalleryImagesArr];
    const combinedImagesMap = {
      ...filteredSelectionImagesMap,
      ...filteredGalleryImagesMapWithTitle
    };

    return {
      filteredImagesArr: combinedImagesArr,
      filteredImagesMap: combinedImagesMap,
      selectionImagesArr: filteredSelectionImagesArr,
      selectionImagesMap: filteredSelectionImagesMap,
      galleryImagesArr: filteredGalleryImagesArr,
      galleryImagesMap: filteredGalleryImagesMapWithTitle
    };
  }, [isSelectPhotoPage, filteredImagesByGallery, selection, collection.type, groupSlug]);

  const fallbackImages = useMemo<IFallbackImages>(
    () => ({
      filteredImagesArr: [] as IndexedImage[],
      filteredImagesMap: {} as IImageMap
    }),
    []
  );

  return selectPhotoImagesData || selectionImagesData || galleryImagesData || fallbackImages;
};
